/**
 * AgreementDialog — collect signatures and finalize a loan agreement.
 *
 * Lifecycle:
 *   open with loanId → POST /loans/{lid}/agreement (idempotent) → populate snapshot preview
 *   user signs, ticks acceptance → PUT /agreements/{aid} (partial saves on Sign+Save)
 *   "Sign & Activate" → POST /agreements/{aid}/sign → loan becomes active
 *   "Activate without agreement (override)" → POST /loans/{lid}/activate-override
 */
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { api, fmtPKR } from "../lib/api";
import SignaturePad from "./SignaturePad";
import { toast } from "sonner";
import { FileText, MessageCircle, Download, ShieldAlert, ImageIcon } from "lucide-react";
import { buildPublicAgreementUrl } from "../lib/publicUrl";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function SnapshotField({ label, value }) {
  return (
    <div className="text-xs">
      <div className="text-slate-500 uppercase tracking-wider font-semibold">{label}</div>
      <div className="text-slate-900 font-medium">{value || "—"}</div>
    </div>
  );
}

function ThumbImg({ url, label }) {
  if (!url) {
    return (
      <div className="aspect-square w-24 rounded-md bg-slate-100 border border-slate-200 flex flex-col items-center justify-center text-slate-400">
        <ImageIcon className="w-5 h-5" />
        <span className="text-[9px] mt-0.5">{label}</span>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <img src={`${API_BASE}${url}`} alt={label} className="aspect-square w-24 rounded-md object-cover border border-slate-200" />
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    </div>
  );
}

export default function AgreementDialog({ open, onOpenChange, loanId, onLoanActivated }) {
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [sigs, setSigs] = useState({ customer: null, owner: null, guarantor: null });

  useEffect(() => {
    if (!open || !loanId) return;
    setLoading(true);
    api.post(`/loans/${loanId}/agreement`)
      .then((r) => {
        setAgreement(r.data);
        setAccepted(!!r.data.customer_accepted);
        setSigs(r.data.signatures || { customer: null, owner: null, guarantor: null });
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load agreement"))
      .finally(() => setLoading(false));
  }, [open, loanId]);

  const save = async (extra = {}) => {
    if (!agreement) return null;
    const { data } = await api.put(`/agreements/${agreement.id}`, {
      customer_accepted: accepted,
      signatures: sigs,
      ...extra,
    });
    setAgreement(data);
    return data;
  };

  const signAndActivate = async () => {
    if (!sigs.customer || !sigs.owner) {
      toast.error("Customer aur Shop Owner ke signatures zaroori hain");
      return;
    }
    if (!accepted) {
      toast.error("Customer ki acceptance tick karein");
      return;
    }
    setBusy(true);
    try {
      await save();
      const { data } = await api.post(`/agreements/${agreement.id}/sign`);
      setAgreement(data);
      toast.success("Agreement signed. Loan is now active.");
      onLoanActivated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign failed");
    } finally {
      setBusy(false);
    }
  };

  const activateWithoutAgreement = async () => {
    if (!window.confirm("Loan ko bina signed agreement ke activate kar dein? Yeh action audit log mein record ho jayega.")) return;
    setBusy(true);
    try {
      await api.post(`/loans/${loanId}/activate-override`);
      toast.success("Loan activated (no agreement) — recorded in audit log.");
      onLoanActivated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Override failed");
    } finally {
      setBusy(false);
    }
  };

  const pdfUrl = agreement
    ? `${API_BASE}/api/agreements/${agreement.id}/pdf?shop=${agreement.shop_id}`
    : null;
  // Customer-facing public URL — never expose backend hostname in WA shares
  const publicPdfUrl = agreement
    ? buildPublicAgreementUrl(agreement.id, agreement.shop_id)
    : null;
  const waMessage = agreement
    ? `Assalam o Alaikum ${agreement.snapshot?.customer_name || ""},\n\nAap ka installment agreement (#${agreement.agreement_number}) tayyar hai. Kindly download kar lijiye:\n${publicPdfUrl}\n\n— ${agreement.snapshot?.shop_name || "HisaabPro"}`
    : "";
  const waLink = agreement?.snapshot?.customer_mobile
    ? `https://wa.me/${(agreement.snapshot.customer_mobile || "").replace(/\D/g, "").replace(/^0/, "92")}?text=${encodeURIComponent(waMessage)}`
    : null;

  const snap = agreement?.snapshot || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="agreement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            Installment Agreement {agreement?.agreement_number ? `· ${agreement.agreement_number}` : ""}
          </DialogTitle>
        </DialogHeader>
        {loading && <div className="text-sm text-slate-500">Loading agreement…</div>}
        {!loading && agreement && (
          <div className="space-y-5">
            {/* Photos row */}
            <div className="flex gap-4">
              <ThumbImg url={snap.customer_photo} label="Customer" />
              <ThumbImg url={snap.guarantor_photo} label="Guarantor" />
              <div className="flex-1 grid grid-cols-2 gap-2 text-sm">
                <SnapshotField label="Shop" value={snap.shop_name} />
                <SnapshotField label="Agreement Date" value={snap.agreement_date} />
                <SnapshotField label="Customer" value={snap.customer_name} />
                <SnapshotField label="Customer CNIC" value={snap.customer_cnic} />
                <SnapshotField label="Guarantor" value={snap.guarantor_name} />
                <SnapshotField label="Guarantor CNIC" value={snap.guarantor_cnic} />
              </div>
            </div>

            {/* Loan terms preview */}
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <SnapshotField label="Product" value={snap.product_name} />
              <SnapshotField label="IMEI / Serial" value={snap.imei_serial} />
              <SnapshotField label="Sale Price" value={fmtPKR(snap.sale_price)} />
              <SnapshotField label="Down Payment" value={fmtPKR(snap.down_payment)} />
              <SnapshotField label="Total Payable" value={fmtPKR(snap.total_payable)} />
              <SnapshotField label="Installment" value={fmtPKR(snap.installment_amount)} />
              <SnapshotField label="Tenure" value={`${snap.tenure || 0} ${snap.installment_type || ""}`} />
              <SnapshotField label="Start Date" value={snap.start_date} />
            </div>

            {/* Acceptance */}
            <label className="flex items-start gap-2 cursor-pointer" data-testid="agreement-accept-row">
              <Checkbox
                checked={accepted}
                onCheckedChange={(v) => setAccepted(!!v)}
                data-testid="agreement-accept-checkbox"
              />
              <div className="text-sm text-slate-700">
                <span className="font-semibold">Customer agrees to the terms</span> of this installment agreement and confirms the information above is correct.
              </div>
            </label>

            {/* Signatures */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SignaturePad
                label="Customer Signature *"
                value={sigs.customer}
                onChange={(v) => setSigs((s) => ({ ...s, customer: v }))}
                testId="sig-customer"
              />
              <SignaturePad
                label="Shop Owner Signature *"
                value={sigs.owner}
                onChange={(v) => setSigs((s) => ({ ...s, owner: v }))}
                testId="sig-owner"
              />
              <SignaturePad
                label="Guarantor Signature (optional)"
                value={sigs.guarantor}
                onChange={(v) => setSigs((s) => ({ ...s, guarantor: v }))}
                testId="sig-guarantor"
              />
            </div>

            {/* Already-signed CTA */}
            {agreement.status === "signed" && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-emerald-700">✓ Agreement signed on {agreement.signed_at?.slice(0,10)}</div>
                  <div className="text-xs text-slate-600">Loan is active. Share the PDF with the customer.</div>
                </div>
                <div className="flex gap-2">
                  <a href={pdfUrl} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded-md text-sm font-semibold"
                     data-testid="agreement-pdf-download">
                    <Download className="w-3.5 h-3.5" /> PDF
                  </a>
                  {waLink && (
                    <a href={waLink} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-sm font-semibold"
                       data-testid="agreement-wa-send">
                      <MessageCircle className="w-3.5 h-3.5" /> Send via WhatsApp
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter className="border-t border-slate-200 pt-3">
          {agreement?.status === "draft" && (
            <>
              <Button type="button" variant="outline" onClick={activateWithoutAgreement} disabled={busy}
                      data-testid="agreement-override-btn"
                      className="text-amber-700 border-amber-400 hover:bg-amber-50">
                <ShieldAlert className="w-4 h-4 mr-1" /> Activate without agreement
              </Button>
              <div className="flex-1" />
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Close</Button>
              <Button type="button" onClick={signAndActivate} disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700" data-testid="agreement-sign-btn">
                {busy ? "Signing…" : "Sign & Activate"}
              </Button>
            </>
          )}
          {agreement?.status === "signed" && (
            <Button type="button" onClick={() => onOpenChange(false)}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
